import ToastMsg from "../components/common/ToastMsg";
import { getNotificationRoute } from "./notificationRoutes";

const MAX_TOAST_LENGTH = 160;

/** All notification_type values written by the backend today. */
export const NOTIFICATION_TYPE_LABELS = {
  Leave: "Leave",
  Reimbursement: "Reimbursement",
  Advance: "Advance",
  Overtime: "Overtime",
  Resignation: "Resignation",
  Exit: "Exit",
  "Transfer Reporting Manager": "Transfer RM",
  Timesheet: "Task",
  Asset: "Asset",
  AttendanceException: "Attendance",
  AttendanceExceptionManager: "Team Attendance",
  AttendanceDiscrepancy: "Punch Discrepancy",
  "Training Assigned": "Training",
  "Training Overdue": "Training Overdue",
  "Appraisal Assigned": "Appraisal",
  "Appraisal Submitted": "Appraisal",
  "Appraisal Reviewed": "Appraisal",
  "Appraisal Sent Back": "Appraisal",
  "Appraisal Published": "Appraisal",
  Payroll: "Payroll",
  System: "System",
  BirthdayAlert: "Birthday",
  "tds declaration": "TDS",
  OccupancyShortage: "Occupancy Shortage",
  "Shift Change": "Shift Change",
  "Shift Assignment Pending": "Shift Assignment",
};

/** Approver alerts for your own submitted request — should never toast to the requester. */
export const isRequesterOwnApprovalAlert = (notification) => {
  if (!notification || notification.notification_action !== "request") {
    return false;
  }
  const myEmployeeId = Number(sessionStorage.getItem("employee_id"));
  if (!myEmployeeId) return false;
  const sentBy = Number(
    notification.sent_by_id ?? notification.sent_by?.id ?? 0
  );
  return sentBy > 0 && sentBy === myEmployeeId;
};

const truncate = (text) => {
  if (!text || text.length <= MAX_TOAST_LENGTH) return text;
  return `${text.slice(0, MAX_TOAST_LENGTH - 3).trim()}...`;
};

export const getNotificationToastType = (notification) => {
  const action = notification?.notification_action;
  const type = notification?.notification_type;

  if (action === "approved" || action === "processed") return "success";
  if (action === "rejected" || action === "cancelled") return "warning";
  if (type === "Training Overdue" || action === "entered") return "error";
  if (action === "upload") return "info";
  return "info";
};

export const buildNotificationToastText = (notification) => {
  const type = notification?.notification_type;
  const label =
    NOTIFICATION_TYPE_LABELS[type] ||
    (type ? String(type) : "Notification");
  const title = (notification?.title || "").trim();
  const message = (notification?.message || "").trim();
  const body =
    title && message
      ? `${title} — ${message}`
      : title || message || "You have a new alert";

  return truncate(`[${label}] ${body}`);
};

const TOAST_DEDUP_MS = 15000;
const recentToastKeys = new Map();

const buildToastDedupKeys = (notification) => {
  const keys = [];
  if (notification?.id != null) {
    keys.push(`id:${notification.id}`);
  }
  keys.push([
    "content",
    notification?.notification_type || "",
    notification?.notification_action || "",
    notification?.type_id ?? "",
    (notification?.title || "").trim(),
    (notification?.message || "").trim(),
  ].join(":"));
  return keys;
};

const pruneRecentToastKeys = (now) => {
  recentToastKeys.forEach((seenAt, key) => {
    if (now - seenAt >= TOAST_DEDUP_MS) {
      recentToastKeys.delete(key);
    }
  });
};

/** Show toast for any notification row — no type filtering. */
export const showNotificationToast = (notification) => {
  if (!notification) return;
  if (isRequesterOwnApprovalAlert(notification)) return;

  const now = Date.now();
  pruneRecentToastKeys(now);

  const keys = buildToastDedupKeys(notification);
  const isDuplicate = keys.some((key) => {
    const seenAt = recentToastKeys.get(key);
    return seenAt != null && now - seenAt < TOAST_DEDUP_MS;
  });
  if (isDuplicate) return;

  keys.forEach((key) => recentToastKeys.set(key, now));

  const typeLabel =
    NOTIFICATION_TYPE_LABELS[notification?.notification_type] ||
    notification?.notification_type ||
    "Notification";
  const title = (notification?.title || "").trim();
  const message = (notification?.message || "").trim();
  const href = getNotificationRoute(notification);

  ToastMsg(getNotificationToastType(notification), null, {
    variant: "notification",
    category: typeLabel,
    title: title || "New notification",
    description: message,
    href: href || undefined,
    actionLabel: href ? "View details" : undefined,
  });
};
